/**
 * Remote configuration — the document schema, the flag registry, and the compiled-in defaults.
 *
 * **Not a `contracts.md` section.** This module is R9's (`docs/prds/supplementary/R9-remote-config-kill-switch.md`),
 * minted as **D327** (the surface: one unauthenticated `GET /config`, served from one operator-owned
 * SSM parameter, response never varies by caller), **D328** (the cache contract: live fetch, then
 * disk cache, then these compiled-in defaults, and no fourth source), and **D329** (the flag
 * registry: a closed set with five columns, three classes, `shabbat_panel` as the first flag). The
 * frozen contract layer does not move and gains no new section for this surface (R9 §1).
 *
 * **What lives here, and what does not.** This file is the *document* — its shape, the server-strict
 * validator (D57's posture, `validation.ts`), the flag registry, and the registry-derived defaults.
 * It does **not** fetch, cache, or evaluate against a live document (`web/src/config/`, R9 §8 S3);
 * it does **not** own the route or the parameter (`infra/data.ts` + `packages/functions/auth/`, R9
 * §8 S2); and it does **not** evaluate a rule object against a cohort — R8
 * (`docs/prds/supplementary/R8-staged-rollout-and-cohorts.md`) owns bucketing and is being built
 * concurrently in a sibling worktree with no module in this tree yet, so a rule object's `salt`/
 * `percent`/`on`/`off` fields are validated here as **opaque, shape-only data** (R9 §3.1/§4.2) and
 * nothing here reads what they mean.
 *
 * **`flag(name)`'s type-restricted name set is `FlagName`, exported below.** R9 §4.1: "the client
 * reads a flag only through one function, `flag(name)`, which the type system restricts to
 * registered names." The function itself — memoized per loaded document, re-read on resume — is
 * `web/src/config/`'s (R9 §8 S3), built against this type; nothing here holds a "currently loaded
 * document" to read one from, which is a client-side, stateful concern this module does not have.
 *
 * **Server strict, client tolerant (R9 §3.1) — and only the strict half lives here.**
 * {@link parseClientConfigDocument} is what `GET /config`'s handler (R9 §8 S2) runs on every
 * parameter read: an undeclared key at any level, an unknown flag name, a malformed rule object, a
 * `notice.text` with no supported locale, or a serialized document over the 4 KB SSM bound is
 * **refused**, never silently repaired or defaulted (R9 §6 — a document that fails validation must
 * not look like success). The client-tolerant half — ignore an unknown flag name, take the registry
 * default for a name the document omits, resolve a rule object to its `off` arm with no evaluator —
 * is `web/src/config/`'s to build (R9 §3.1, §4.2) and is **not implemented in this module**.
 *
 * **Browser-bundleable (`AGENTS.md` §4.6).** No `node:` specifier at module scope. The 4 KB byte
 * bound is measured with the global `TextEncoder`, present in both Node ≥ 11 and every browser —
 * the same "one CSPRNG on both sides" reasoning `gate-id.ts` gives for `globalThis.crypto`.
 */
import { ValidationError, parseBody } from './validation.js';
/**
 * **The closed set.** `FlagName` (below) is derived from this array's own `name` fields — R9 §8
 * S1's own done-when: "the compiled-in defaults derived from the registry — one source, so defaults
 * cannot drift from declarations." Adding a flag is one row here; nothing else declares a flag name.
 *
 * **`shabbat_panel` is the first flag (R9 §4.3/§9 D329), a migration and not a product change.** It
 * existed as `SHABBAT_PANEL_ENABLED` — the panel's own per-stage compile-time switch, deleted by
 * R9 §8 S3 in favour of `flag('shabbat_panel')` — and going remote changes nothing a resident
 * sees while proving the whole rail end to end (R9 §4.3). Its default is `true` because that is
 * what the compile-time switch ships today; the panel does not vanish for a client running on
 * compiled-in defaults.
 */
export const FLAG_REGISTRY = [
    {
        name: 'shabbat_panel',
        default: true,
        class: 'surface',
        decision: 'D329',
        removalDate: 'the end of the first production exercise (R9 §8 S6) — 🔸 author proposal, not yet taken',
    },
];
/** Fast membership test, built once from {@link FLAG_REGISTRY} rather than re-derived per call. */
const FLAG_NAMES = new Set(FLAG_REGISTRY.map((entry) => entry.name));
function isFlagName(candidate) {
    return FLAG_NAMES.has(candidate);
}
function flagDefault(name) {
    const entry = FLAG_REGISTRY.find((candidate) => candidate.name === name);
    // Unreachable while FlagName is derived from FLAG_REGISTRY's own names — a defensive throw
    // rather than a non-null assertion, so a future refactor that breaks that link fails loudly.
    if (entry === undefined) {
        throw new Error(`no registry entry for flag "${name}" — FlagName has drifted from FLAG_REGISTRY`);
    }
    return entry.default;
}
const RULE_OBJECT_FIELDS = ['salt', 'percent', 'on', 'off'];
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** Whether `value` is a well-formed {@link FlagRuleObject} — exposed so the shape is testable. */
export function isFlagRuleObject(value) {
    if (!isPlainObject(value))
        return false;
    const keys = Object.keys(value);
    if (keys.length !== RULE_OBJECT_FIELDS.length)
        return false;
    for (const field of RULE_OBJECT_FIELDS) {
        if (!(field in value))
            return false;
    }
    const salt = value['salt'];
    const percent = value['percent'];
    const on = value['on'];
    const off = value['off'];
    if (typeof salt !== 'string' || salt.length === 0)
        return false;
    // **`percent` is an INTEGER 0-100, and the integrality is load-bearing** (R8 §3.2, which
    // states it in those words). `Number.isFinite` was the looser first read of R9 §8 S1's
    // "validate the rule opaquely", and loose here is not conservative: D57 makes this server
    // STRICT, and `resolveFlagArm` in `cohort.ts` treats a non-integer `percent` as malformed and
    // fails closed to `off`. A server that accepted `percent: 50.5` would serve a document every
    // device reads as *this flag is off for everyone*, with `config_version` advancing, the route
    // answering `200`, and `ClientConfigInvalid` never emitted -- an operator believing a flag is
    // at half rollout while it is off, and no instrument anywhere disagreeing. The two validators
    // must agree, and R8 §3.2 says which way. **The agreement is now asserted rather than
    // described** (R8 §4 S2): `cohort.test.ts`'s *every rule the DOCUMENT accepts is a rule the
    // EVALUATOR can evaluate* pins the implication in the one direction that costs anything --
    // schema-accepted implies evaluable -- and a loosening of this line reddens it twice, once on
    // which candidates are accepted and once on the arm a newly-accepted rule resolves to.
    if (typeof percent !== 'number' || !Number.isInteger(percent) || percent < 0 || percent > 100) {
        return false;
    }
    if (typeof on !== 'boolean' || typeof off !== 'boolean')
        return false;
    return true;
}
// ---------------------------------------------------------------------------------------------
// The notice (R9 §3.1/§4.5) — locale-keyed, no markup, dismissed per id
// ---------------------------------------------------------------------------------------------
/**
 * The five-locale set this document's `notice.text` is checked against.
 *
 * 🔸 **A deliberate fifth copy, on `adding-a-locale.md`'s own "five layers" precedent** — `LOCALES`
 * (`web/src/i18n/locales.ts`), `SUPPORTED_LOCALES` (`packages/functions/auth/config.ts`) and
 * `SHARE_LOCALES` (`packages/functions/members/config.ts`) are each their own copy already, because
 * nothing here may import `web/` and `packages/functions/auth` may not become this module's
 * dependent just to share one array. Widening the product's locale set means widening this array
 * in the same change as the other four (`adding-a-locale.md` §6's fallback order applies to
 * `notice.text` the same way it applies to a catalog).
 */
export const CLIENT_CONFIG_LOCALES = ['he', 'en', 'ru', 'it', 'uk'];
/** SSM's standard tier (R9 §2.2). The advanced tier is a second billing line for one string. */
export const CLIENT_CONFIG_MAX_BYTES = 4096;
const DOCUMENT_FIELDS = [
    'config_version',
    'min_build_number',
    'prompt_build_number',
    'flags',
    'notice',
];
const NOTICE_FIELDS = ['id', 'text'];
function ensureNoUnknownKeys(value, allowed, field) {
    const declared = new Set(allowed);
    for (const key of Object.keys(value)) {
        if (!declared.has(key)) {
            throw new ValidationError(`${field}.${key}`, 'unknown field');
        }
    }
}
function parseConfigVersion(raw) {
    if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) {
        throw new ValidationError('config_version', 'must be an integer of at least 0');
    }
    return raw;
}
/**
 * Both floors share one rule, because they compare one counter (D261): an integer of at least 0,
 * or absent. **The field name is a parameter so the refusal names the key the operator wrote.**
 */
function parseBuildNumber(raw, field) {
    if (raw === undefined)
        return undefined;
    if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) {
        throw new ValidationError(field, 'must be an integer of at least 0');
    }
    return raw;
}
function parseFlagValue(raw, name) {
    if (typeof raw === 'boolean')
        return raw;
    if (isFlagRuleObject(raw))
        return raw;
    throw new ValidationError(`flags.${name}`, 'must be a boolean or a rule object {salt,percent,on,off}');
}
function parseFlags(raw) {
    if (!isPlainObject(raw)) {
        throw new ValidationError('flags', 'must be an object');
    }
    const flags = {};
    for (const [name, value] of Object.entries(raw)) {
        if (!isFlagName(name)) {
            throw new ValidationError(`flags.${name}`, 'unknown flag name');
        }
        flags[name] = parseFlagValue(value, name);
    }
    return flags;
}
function parseNoticeText(raw) {
    if (!isPlainObject(raw)) {
        throw new ValidationError('notice.text', 'must be an object');
    }
    const entries = Object.entries(raw);
    if (entries.length === 0) {
        throw new ValidationError('notice.text', 'must carry at least one locale');
    }
    const text = {};
    for (const [locale, value] of entries) {
        if (!CLIENT_CONFIG_LOCALES.includes(locale)) {
            throw new ValidationError(`notice.text.${locale}`, 'not a locale this product serves');
        }
        if (typeof value !== 'string' || value.length === 0) {
            throw new ValidationError(`notice.text.${locale}`, 'must be a non-empty string');
        }
        text[locale] = value;
    }
    return text;
}
function parseNotice(raw) {
    if (raw === undefined)
        return undefined;
    if (!isPlainObject(raw)) {
        throw new ValidationError('notice', 'must be an object');
    }
    ensureNoUnknownKeys(raw, NOTICE_FIELDS, 'notice');
    const id = raw['id'];
    if (typeof id !== 'string' || id.length === 0) {
        throw new ValidationError('notice.id', 'must be a non-empty string');
    }
    const text = parseNoticeText(raw['text']);
    return { id, text };
}
/** UTF-8 byte length — a codepoint count or `.length` would under-count Hebrew/Cyrillic text. */
const textEncoder = new TextEncoder();
/**
 * The one document parser (R9 §8 S1). **Server strict** (D57's posture): an undeclared key at any
 * level, an unknown flag name, a malformed rule object, a `notice.text` naming no served locale, or
 * a serialized document over {@link CLIENT_CONFIG_MAX_BYTES} throws `ValidationError` — never
 * silently repaired, stripped, or defaulted (R9 §6). This is the validator `GET /config`'s handler
 * runs on every parameter read (R9 §8 S2); it is not the client's tolerant reader (R9 §3.1) and
 * does not resolve a rule object or fall back to a registry default — the caller decides what a
 * thrown `ValidationError` means for the fetch that produced `raw`.
 */
export function parseClientConfigDocument(raw) {
    const schema = {
        fields: DOCUMENT_FIELDS,
        parse: (body) => {
            const config_version = parseConfigVersion(body['config_version']);
            const min_build_number = parseBuildNumber(body['min_build_number'], 'min_build_number');
            const prompt_build_number = parseBuildNumber(body['prompt_build_number'], 'prompt_build_number');
            const flags = parseFlags(body['flags']);
            const notice = parseNotice(body['notice']);
            // Optional keys are spread in only when present, so an absent key stays absent on the wire
            // rather than serializing as `undefined` — the byte count below measures what is served.
            const document = {
                config_version,
                ...(min_build_number === undefined ? {} : { min_build_number }),
                ...(prompt_build_number === undefined ? {} : { prompt_build_number }),
                flags,
                ...(notice === undefined ? {} : { notice }),
            };
            const byteLength = textEncoder.encode(JSON.stringify(document)).length;
            if (byteLength > CLIENT_CONFIG_MAX_BYTES) {
                throw new ValidationError('body', `must serialize to at most ${CLIENT_CONFIG_MAX_BYTES} bytes (was ${byteLength})`);
            }
            return document;
        },
    };
    return parseBody(schema, raw);
}
// ---------------------------------------------------------------------------------------------
// Compiled-in defaults (R9 §3.2's third source) — derived from the registry, never hand-listed
// ---------------------------------------------------------------------------------------------
/**
 * The third and last cache source (R9 §3.2): a wiped-cache, no-network launch runs on this. No
 * floor, no notice, `config_version: 0` — and every flag's value is {@link FLAG_REGISTRY}'s own
 * `default`, read from the one place it is declared so a compiled default can never drift from the
 * registry that declares it (R9 §8 S1's own done-when).
 */
export const CLIENT_CONFIG_DEFAULTS = {
    config_version: 0,
    flags: Object.fromEntries(FLAG_REGISTRY.map((entry) => [entry.name, flagDefault(entry.name)])),
};
//# sourceMappingURL=client-config.js.map